/**
 * DocumentTypeSelector Component
 *
 * Displays available document types organized by category for selection.
 */

import type { LucideIcon } from 'lucide-react';
import {
  FileWarning,
  ClipboardCheck,
  GraduationCap,
  Settings,
  FileText,
  Heart,
  Stethoscope,
  FolderOpen,
  PenLine,
  ClipboardList,
  ArrowRight,
} from 'lucide-react';
import {
  DocumentGenerationType,
  DocumentTypeInfo,
  DocumentCategory,
  DOCUMENT_TYPES,
  CATEGORY_STYLES,
} from '../../types/generatedDoc';

interface DocumentTypeSelectorProps {
  selectedType: DocumentGenerationType | null;
  onSelect: (type: DocumentGenerationType) => void;
  /** Optional callback for the "Fill a Form" action — renders a callout between Medical and Custom */
  onFillFormClick?: () => void;
}

// Icon mapping for document types
const ICON_MAP: Record<string, LucideIcon> = {
  FileWarning,
  ClipboardCheck,
  GraduationCap,
  Settings,
  FileText,
  Heart,
  Stethoscope,
  FolderOpen,
  PenLine,
};

// Group document types by category
const CATEGORIES: Array<{ key: DocumentCategory; label: string; description: string }> = [
  {
    key: 'insurance',
    label: 'Insurance',
    description: 'Appeals and authorization requests',
  },
  {
    key: 'education',
    label: 'Education',
    description: 'IEP and school documentation',
  },
  {
    key: 'benefits',
    label: 'Benefits',
    description: 'Government assistance applications',
  },
  {
    key: 'medical',
    label: 'Medical',
    description: 'Provider communication and records',
  },
  {
    key: 'custom',
    label: 'Custom',
    description: 'Create your own document',
  },
];

function getDocumentsByCategory(category: DocumentCategory): DocumentTypeInfo[] {
  return DOCUMENT_TYPES.filter((doc) => doc.category === category);
}

export function DocumentTypeSelector({
  selectedType,
  onSelect,
  onFillFormClick,
}: DocumentTypeSelectorProps): JSX.Element {
  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-ink mb-2">
          What type of document do you need?
        </h2>
        <p className="text-muted">
          Select the document type that best matches your needs
        </p>
      </div>

      {CATEGORIES.map((category) => {
        const categoryDocs = getDocumentsByCategory(category.key);
        const style = CATEGORY_STYLES[category.key];

        return (
          <div key={category.key}>
            {/* Form Filler callout — rendered before the Custom category */}
            {category.key === 'custom' && onFillFormClick && (
              <button
                type="button"
                onClick={onFillFormClick}
                className="
                  w-full mb-8 p-5 rounded-lg border-2 border-dashed
                  border-evergreen-200 bg-evergreen-50/50
                  hover:border-evergreen-400 hover:bg-evergreen-50
                  transition-all text-left group
                "
              >
                <div className="flex items-center gap-4">
                  <div className="p-2.5 rounded-lg bg-evergreen-100 text-evergreen group-hover:bg-evergreen group-hover:text-white transition-colors">
                    <ClipboardList size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-ink">
                      Have a form to fill out?
                    </h3>
                    <p className="text-sm text-muted mt-0.5">
                      Upload any blank form (Medicaid renewal, school enrollment, insurance claim)
                      and we'll auto-fill it from your CaseFile data
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center gap-1 text-evergreen font-medium text-sm flex-shrink-0 group-hover:translate-x-0.5 transition-transform">
                    Fill a Form
                    <ArrowRight size={16} />
                  </div>
                </div>
              </button>
            )}

            <div className="flex items-center gap-3 mb-3">
              <span className={`badge ${style.bg} ${style.text}`}>
                {category.label}
              </span>
              <span className="text-sm text-muted">{category.description}</span>
            </div>

            <div className={`grid grid-cols-1 ${category.key === 'custom' ? '' : 'sm:grid-cols-2'} gap-3`}>
              {categoryDocs.map((docType) => {
                const Icon = ICON_MAP[docType.icon] || FileText;
                const isSelected = selectedType === docType.type;
                const isCustom = docType.type === 'custom';

                return (
                  <button
                    key={docType.type}
                    onClick={() => onSelect(docType.type)}
                    className={`
                      p-4 rounded-lg border-2 text-left transition-all
                      ${isCustom && !isSelected ? 'border-dashed border-violet-300 hover:border-violet-400 hover:bg-violet-50' : ''}
                      ${isCustom && isSelected ? 'border-solid border-evergreen bg-evergreen/5 shadow-sm' : ''}
                      ${!isCustom && isSelected ? 'border-evergreen bg-evergreen/5 shadow-sm' : ''}
                      ${!isCustom && !isSelected ? 'border-soft-neutral hover:border-evergreen/50 hover:bg-sage/30' : ''}
                    `}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`
                          p-2 rounded-lg
                          ${isSelected ? 'bg-evergreen text-white' : isCustom ? 'bg-violet-100 text-violet-600' : 'bg-sage text-evergreen'}
                        `}
                      >
                        <Icon size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3
                          className={`font-medium ${
                            isSelected ? 'text-evergreen' : 'text-ink'
                          }`}
                        >
                          {docType.name}
                        </h3>
                        <p className="text-sm text-muted line-clamp-2">
                          {docType.description}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default DocumentTypeSelector;
